import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

/**
 * De basis-URL voor video's, logo's en andere bestanden uit de opslag —
 * inclusief het kortlevende inlogbewijs dat `/storage/` sindskort eist.
 *
 * Waarom dit een dienst is en niet gewoon `environment.storageUrl`: het
 * bewijs is dynamisch (per sessie gemint) en `<img src>`/`<video src>`
 * kunnen geen Authorization-kop meekrijgen. Het bewijs reist daarom mee ALS
 * onderdeel van de URL zelf: `.../storage/<bewijs>/<pad>`. Bestaande code
 * die `storageUrl + pad` samenvoegt blijft daardoor ongewijzigd werken —
 * alleen de bron van `storageUrl` verandert van een vaste tekst in deze
 * dienst.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageUrlService {

  private token: string = null;

  constructor(private http: HttpClient) {
  }

  /**
   * De actuele basis-URL, met bewijs. Leeg zolang er nog geen bewijs is
   * opgehaald — een `<img>` met een lege bron toont dan kort niets, en
   * herstelt zichzelf zodra {@link refresh} klaar is, omdat elke aanroeper
   * deze getter opnieuw leest.
   */
  public get base(): string {
    return this.token ? `${environment.storageUrl}${this.token}/` : '';
  }

  /**
   * Een nieuw bewijs ophalen. Aanroepen na elke geslaagde inlog, en bij het
   * opstarten van de app als er nog een sessie in de opslag van de browser
   * staat.
   */
  public async refresh(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ token: string }>(`${environment.apiUrl}/storage/token`)
      );
      this.token = res?.token ?? null;
    } catch {
      this.token = null;
    }
  }

  /** Bij uitloggen: geen bewijs van de vorige gebruiker laten rondslingeren. */
  public clear(): void {
    this.token = null;
  }

  /**
   * Absolute afspeel-URL via de sessie-sat (`/storage/<sat>/pad`).
   * Alleen voor eigen UI — partners niet (sessie-token, ~24u, verkeerde club-scope).
   */
  public async urlForPath(path: string): Promise<string | null> {
    const key = (path || '').replace(/^\/+/, '').trim();
    if (!key) {
      return null;
    }
    if (!this.token) {
      await this.refresh();
    }
    if (!this.token) {
      return null;
    }
    return `${environment.storageUrl}${this.token}/${key}`;
  }

  /**
   * Standaard partner-videolink (zelfde als League-overzicht → Brook):
   * `/storage/<partner-token>/<pad>` — club-scoped. Met de wedstrijd werkt
   * hij tot de partner de analyse heeft geleverd, anders 7 dagen.
   * Club = eerste padsegment (`10014/videos/…`).
   */
  public async partnerUrlForPath(path: string, match?: string): Promise<string | null> {
    const key = (path || '').replace(/^\/+/, '').trim();
    if (!key) {
      return null;
    }
    const club = key.split('/')[0];
    if (!club || !/^\d+$/.test(club)) {
      return null;
    }
    try {
      let params = new HttpParams().set('club', club);
      if (match) {
        params = params.set('match', match);
      }
      const res = await firstValueFrom(
        this.http.get<{ token?: string }>(`${environment.apiUrl}/storage/partner-token`, { params })
      );
      const partnerToken = (res?.token || '').trim();
      if (!partnerToken) {
        return null;
      }
      return `${environment.storageUrl}${partnerToken}/${key}`;
    } catch {
      return null;
    }
  }
}
